import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "/api"
    : "http://localhost:8075/api");

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

const tryParseBlobError = async (error) => {
  const response = error?.response;
  const data = response?.data;

  if (!(data instanceof Blob)) {
    return error;
  }

  const contentType = response?.headers?.["content-type"] || data.type || "";
  if (!contentType.includes("json") && !contentType.includes("text")) {
    return error;
  }

  try {
    const text = await data.text();
    if (!text) {
      response.data = { detail: "" };
      return error;
    }

    try {
      response.data = JSON.parse(text);
    } catch {
      response.data = { detail: text };
    }
  } catch {
    return error;
  }

  return error;
};

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("username");
      window.location.replace("/login");
    }

    return Promise.reject(await tryParseBlobError(error));
  }
);

const newRequestKey = () => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

// Une nouvelle tentative après une réponse perdue conserve la même identité.
const creation = async (method, path, payload = {}) => {
  const owner = sessionStorage.getItem("username") || "local";
  const identity = JSON.stringify([owner, method, path, payload]);
  const storageKey = "facturalite:requests";
  let pending;
  try { pending = JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch { pending = {}; }
  const now = Date.now();
  Object.keys(pending).forEach((key) => { if (now - pending[key].at > 6 * 86400000) delete pending[key]; });
  const entry = pending[identity] || { key: newRequestKey(), at: now };
  pending[identity] = entry;
  const persist = () => { try { sessionStorage.setItem(storageKey, JSON.stringify(pending)); } catch {} };
  persist();
  try {
    const result = await api.request({ method, url: path, data: payload, headers: { "Idempotency-Key": entry.key } });
    // Relire pour préserver les requêtes concurrentes.
    try { pending = JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch {}
    delete pending[identity]; persist();
    return result;
  } catch (error) {
    if (error.response?.status >= 400 && error.response?.status < 500 && ![401, 408, 429].includes(error.response.status)) {
      try { pending = JSON.parse(sessionStorage.getItem(storageKey) || "{}"); } catch {}
      delete pending[identity]; persist();
    }
    throw error;
  }
};

export const authAPI = {
  login: (username, password) => api.post("/auth/login", { username, password }, { skipAuth: true, skipAuthRedirect: true }),
  check: (signal) => api.get("/auth/check", { signal, skipAuthRedirect: true }),
  logout: () => api.post("/auth/logout", {}, { skipAuthRedirect: true }),
  changePassword: (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }, { skipAuthRedirect: true }),
};

export const clientsAPI = {
  archive: (id) => api.post(`/clients/${id}/archive`),
  restore: (id) => api.post(`/clients/${id}/restore`),
  billing: (id) => api.get(`/clients/${id}/facturation`),
  getAll: () => api.get("/clients"),
  create: (client) => creation("post", "/clients", client),
  update: (id, client) => api.put(`/clients/${id}`, client),
};

export const backupAPI = { exportData: () => api.get("/export") };

export const facturesAPI = {
  getAll: () => api.get("/factures"),
  getOne: (id) => api.get(`/factures/${id}`),
  create: (facture) => creation("post", "/factures", facture),
  update: (id, facture) => creation("put", `/factures/${id}`, facture),
  previewPDF: (facture) =>
    api.post("/factures/pdf/preview", facture, { responseType: "blob" }),
  markPaid: (id) => api.post(`/factures/${id}/pay`),
  cancel: (id) => api.post(`/factures/${id}/cancel`),
  sendEmail: (id) => api.post(`/factures/${id}/email`),
  verifyInitialDelivery: (id, eventId, envoyee) => api.post(`/factures/${id}/envois/${eventId}/verification`, { envoyee }),
  sendRelance: (id) => api.post(`/factures/${id}/relance`),
  generatePDF: (id) =>
    api.post(`/factures/${id}/pdf`, {}, { responseType: "blob" }),
};

export const relancesAPI = {
  confirmPenaltyConditions: (id, conditions) => api.post(`/relances/factures/${id}/penalites/conditions`, conditions),
  pausePenalties: (id, enPause) => api.patch(`/relances/factures/${id}/penalites/pause`, { en_pause: enPause }),
  preview: (options) => api.post("/relances/preview", options),
  previewEmail: (id, palier) => api.get(`/relances/factures/${id}/email-preview`, {
    params: palier === null ? {} : { palier },
  }),
  previewAttachment: (id) => api.get(`/relances/factures/${id}/email-preview/pdf`, { responseType: "blob" }),
  pause: (id, enPause) => api.patch(`/relances/factures/${id}/pause`, { en_pause: enPause }),
  confirmInvoiceSent: (id) => api.post(`/relances/factures/${id}/confirmer-envoi`),
  verifyDelivery: (id, eventId, envoyee) =>
    api.post(`/relances/factures/${id}/historique/${eventId}/verification`, { envoyee }),
};

export const abonnementsAPI = {
  archive: (id) => api.post(`/abonnements/${id}/archive`),
  restore: (id) => api.post(`/abonnements/${id}/restore`),
  getAll: () => api.get("/abonnements"),
  create: (abonnement) => creation("post", "/abonnements", abonnement),
  update: (id, abonnement) => api.put(`/abonnements/${id}`, abonnement),
  delete: (id) => api.delete(`/abonnements/${id}`),
  getEcheances: () => api.get("/abonnements/echeances"),
  facturer: (id) => creation("post", `/abonnements/${id}/facturer`),
  facturerEcheances: () => creation("post", "/abonnements/facturer-echeances"),
};

export const sourceBillingAPI = {
  preview: (kind, id) => api.get(`/${kind}/${id}/conditions-facturation`),
  confirm: (kind, id, conditions) => api.post(`/${kind}/${id}/conditions-facturation`, {
    accord_confirme: true,
    conditions_attendues: conditions,
  }),
};

export const devisAPI = {
  sendSignature: (id) => api.post(`/devis/${id}/signature`),
  cancelSignature: (id) => api.post(`/devis/${id}/signature/annuler`),
  newVersion: (id) => creation("post", `/devis/${id}/nouvelle-version`),
  signatureHistory: (id) => api.get(`/devis/${id}/signatures`),
  signatureConfirmations: (id, requestId) => api.post(`/devis/${id}/signatures/${requestId}/confirmations`),
  signatureArchive: (id, requestId, kind) => api.get(`/devis/${id}/signatures/${requestId}/${kind}`, { responseType: "blob" }),
  getAll: () => api.get("/devis"),
  getOne: (id) => api.get(`/devis/${id}`),
  getTemplate: () => api.get("/devis/template"),
  create: (payload) => creation("post", "/devis", payload),
  update: (id, payload) => api.put(`/devis/${id}`, payload),
  updateFacturationPlan: (id, payload) =>
    api.put(`/devis/${id}/facturation-plan`, payload),
  invoicePalier: (id, palierId) =>
    creation("post", `/devis/${id}/facturer-palier/${palierId}`),
  previewPDF: (payload) =>
    api.post("/devis/preview", payload, { responseType: "blob" }),
  downloadPDF: (id) => api.get(`/devis/${id}/pdf`, { responseType: "blob" }),
  downloadJSON: (id) => api.get(`/devis/${id}/json`, { responseType: "blob" }),
  sendEmail: (id) => api.post(`/devis/${id}/email`),
  archive: (id) => api.post(`/devis/${id}/archive`),
  restore: (id) => api.post(`/devis/${id}/restore`),
  delete: (id) => api.delete(`/devis/${id}`),
};

const publicSignatureOptions = { skipAuth: true, skipAuthRedirect: true };
export const signatureAPI = {
  get: (token, signal) => api.get(`/public/signatures/${token}`, { ...publicSignatureOptions, signal }),
  pdf: (token, signal) => api.get(`/public/signatures/${token}/pdf`, { ...publicSignatureOptions, responseType: "blob", signal }),
  code: (token) => api.post(`/public/signatures/${token}/code`, {}, publicSignatureOptions),
  sign: (token, payload) => api.post(`/public/signatures/${token}/signer`, payload, publicSignatureOptions),
  refuse: (token) => api.post(`/public/signatures/${token}/refuser`, {}, publicSignatureOptions),
};

export const markdownAPI = {
  archive: (id) => api.post(`/markdown/documents/${id}/archive`),
  restore: (id) => api.post(`/markdown/documents/${id}/restore`),
  getAll: () => api.get("/markdown/documents"),
  getOne: (id) => api.get(`/markdown/documents/${id}`),
  create: (payload) => creation("post", "/markdown/documents", payload),
  update: (id, payload) => api.put(`/markdown/documents/${id}`, payload),
  downloadPDF: (id) => api.get(`/markdown/documents/${id}/pdf`, { responseType: "blob" }),
  renderPDF: (payload, options = {}) =>
    api.post("/markdown/pdf", payload, { ...options, responseType: "blob" }),
};

export const parametresAPI = {
  get: () => api.get("/parametres"),
  update: (parametres) => api.put("/parametres", parametres),
  smtpTest: (payload) => api.post("/parametres/smtp-test", payload),
};

export const prestationsAPI = {
  getAll: () => api.get("/prestations"),
  create: (prestation) => creation("post", "/prestations", prestation),
  update: (id, prestation) => api.put(`/prestations/${id}`, prestation),
  delete: (id) => api.delete(`/prestations/${id}`),
};

export const tachesAPI = {
  getAll: (params, signal) => api.get("/taches", { params, signal }),
  create: (tache) => creation("post", "/taches", tache),
  patch: (id, payload) => api.patch(`/taches/${id}`, payload),
  delete: (id) => api.delete(`/taches/${id}`),
  getAFacturer: (clientId) =>
    api.get("/taches/a-facturer", { params: { client_id: clientId } }),
  addTimeEntry: (id, payload) => creation("post", `/taches/${id}/time-entries`, payload),
  updateTimeEntry: (id, entryId, payload) =>
    api.patch(`/taches/${id}/time-entries/${entryId}`, payload),
  deleteTimeEntry: (id, entryId) =>
    api.delete(`/taches/${id}/time-entries/${entryId}`),
};

export default api;
